import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse } from '@/api/types';
import { getDeclarationOptions, getPeopleNames, type DeclarationOptions } from '@/api/users';
import { Loader2, Home, X, Plus } from 'lucide-react';

interface HeritageStepProps {
  onComplete: () => void;
}

interface IndianState {
  code: string;
  name: string;
}

interface Language {
  code: string;
  name: string;
  nativeName?: string;
}

// Matches the festival religion enum (compared case-insensitively
// server-side) so festival reminders can find their recipients.
const RELIGIONS = [
  { value: 'hindu', label: 'Hindu' },
  { value: 'sikh', label: 'Sikh' },
  { value: 'muslim', label: 'Muslim' },
  { value: 'jain', label: 'Jain' },
  { value: 'buddhist', label: 'Buddhist' },
  { value: 'christian', label: 'Christian' },
  { value: 'zoroastrian', label: 'Zoroastrian (Parsi)' },
  { value: 'jewish', label: 'Jewish' },
  { value: 'multi_faith', label: 'Multi-faith' },
  { value: 'spiritual', label: 'Spiritual' },
  { value: 'other', label: 'Other' },
];

// The same values the Android picker sends: the first year of the decade. The
// server stores the decade on the Identity Board (see onboarding/decade.ts).
const DECADE_OPTIONS = [
  { value: '1950', label: '1950s' },
  { value: '1960', label: '1960s' },
  { value: '1970', label: '1970s' },
  { value: '1980', label: '1980s' },
  { value: '1990', label: '1990s' },
  { value: '2000', label: '2000s' },
  { value: '2010', label: '2010s' },
  { value: '2020', label: '2020s' },
];

// Where the family is from, in the shape every diaspora life fits
// (api shared/identity/declaration.ts). Every answer is allowed, including
// no answer; nobody has to pick a state they are not from.
type RootsStatus = 'given' | 'not_sure' | 'none' | 'rather_not_say';
type GrewUpStatus = 'with_roots' | 'given' | 'rather_not_say';
type RootKind = 'indian_state' | 'community' | 'place' | 'diaspora';

const ROOTS_ANSWERS: { value: RootsStatus; label: string }[] = [
  { value: 'given', label: 'A place or a people' },
  { value: 'not_sure', label: 'Not sure' },
  { value: 'none', label: 'None of these' },
  { value: 'rather_not_say', label: 'Rather not say' },
];

const GREW_UP_ANSWERS: { value: GrewUpStatus; label: string }[] = [
  { value: 'with_roots', label: 'Where the family is from' },
  { value: 'given', label: 'Somewhere else' },
  { value: 'rather_not_say', label: 'Rather not say' },
];

// The first list on the server (api shared/identity/peoples.ts). The reviewed
// people-names list replaces it in the next phase.
const COMMUNITIES = [
  { value: 'sindhi', label: 'Sindhi' },
  { value: 'tibetan', label: 'Tibetan' },
];
const DIASPORA = [
  { value: 'indo_caribbean', label: 'Indo-Caribbean' },
  { value: 'indo_trinidadian', label: 'Indo-Trinidadian' },
  { value: 'indo_guyanese', label: 'Indo-Guyanese' },
  { value: 'indo_fijian', label: 'Indo-Fijian' },
  { value: 'mauritian', label: 'Mauritian' },
  { value: 'east_african_gujarati', label: 'East African Gujarati' },
];

const MAX_ENTRIES = 5;

interface RootRow {
  kind: RootKind;
  /** State code, community or diaspora id, or the place outside India. */
  value: string;
  /** The family's town, for an Indian state. */
  town: string;
}

interface PlaceRow {
  country: string;
  region: string;
  town: string;
}

function rootPayload(row: RootRow) {
  switch (row.kind) {
    case 'indian_state':
      return { kind: row.kind, code: row.value, place: row.town.trim() ? { text: row.town.trim() } : null };
    case 'place':
      return { kind: row.kind, place: { text: row.value.trim() } };
    default:
      return { kind: row.kind, code: row.value };
  }
}

function placePayload(row: PlaceRow) {
  const inIndia = row.country.trim().toUpperCase() === 'IN';
  return {
    country: row.country.trim(),
    region: inIndia && row.region ? row.region : null,
    place: row.town.trim() ? { text: row.town.trim() } : null,
  };
}

export default function HeritageStep({ onComplete }: HeritageStepProps) {
  const [rootsStatus, setRootsStatus] = useState<RootsStatus>('given');
  const [roots, setRoots] = useState<RootRow[]>([{ kind: 'indian_state', value: '', town: '' }]);
  const [marriedInto, setMarriedInto] = useState('');
  const [grewUpStatus, setGrewUpStatus] = useState<GrewUpStatus>('with_roots');
  const [places, setPlaces] = useState<PlaceRow[]>([{ country: 'IN', region: '', town: '' }]);
  const [nativeLanguage, setNativeLanguage] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [religion, setReligion] = useState('');
  const [era, setEra] = useState('');
  // "How do you say it?": the server composes every way; the member picks.
  const [ways, setWays] = useState<DeclarationOptions | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const [peopleName, setPeopleName] = useState<string[]>([]);

  // Fetch Indian states
  const { data: statesData } = useQuery({
    queryKey: ['reference', 'indian-states'],
    queryFn: () => api.get<ApiResponse<{ items: IndianState[] }>>('/reference/indian-states'),
  });

  // Fetch languages
  const { data: languagesData } = useQuery({
    queryKey: ['reference', 'languages'],
    queryFn: () => api.get<ApiResponse<{ items: Language[] }>>('/reference/languages'),
  });

  const states = statesData?.data?.items || [];
  const languageOptions = languagesData?.data?.items || [];

  const filledRoots = roots.filter((row) => row.value.trim());
  const filledPlaces = places.filter((row) => row.country.trim());

  // Only reviewed people-names are served; an empty list means none is reviewed yet.
  const { data: peopleNamesData } = useQuery({
    queryKey: ['reference', 'people-names'],
    queryFn: () => getPeopleNames(),
  });
  const reviewedCount = peopleNamesData?.data?.items.length ?? 0;

  const rootsBody = () => ({
    status: rootsStatus,
    entries: rootsStatus === 'given' ? filledRoots.map(rootPayload) : [],
    byMarriage: marriedInto ? [{ kind: 'indian_state', code: marriedInto }] : [],
  });
  const grewUpBody = () => ({
    status: grewUpStatus,
    places: grewUpStatus === 'given' ? filledPlaces.map(placePayload) : [],
  });

  const showWays = useMutation({
    mutationFn: (picks: string[]) =>
      getDeclarationOptions({
        roots: rootsBody(),
        grewUp: grewUpBody(),
        peopleName: picks,
        motherTongue: nativeLanguage || null,
      }),
    onSuccess: (res) => {
      setWays(res.data);
      // A pick these answers no longer make reads as the default.
      if (choice && !res.data.options.some((o) => o.id === choice)) setChoice(null);
    },
  });

  const togglePeopleName = (id: string) => {
    const next = peopleName.includes(id) ? peopleName.filter((p) => p !== id) : [...peopleName, id];
    setPeopleName(next);
    showWays.mutate(next);
  };

  const saveStep = useMutation({
    mutationFn: async () => {
      const step4 = await api.patch('/onboarding/step/4', {
        roots: rootsBody(),
        grewUp: grewUpBody(),
        peopleName,
        declaration: { choice },
        nativeLanguage,
        languages,
        era: era || undefined,
      });
      // Religion lives in the cultural step (5) — saved here so festival
      // reminders have recipients without a separate wizard screen.
      if (religion) {
        await api.patch('/onboarding/step/5', { religion });
      }
      return step4;
    },
    onSuccess: () => {
      onComplete();
    },
  });

  const handleAddLanguage = (code: string) => {
    if (!languages.includes(code) && languages.length < 5) {
      setLanguages([...languages, code]);
    }
  };

  const handleRemoveLanguage = (code: string) => {
    setLanguages(languages.filter((l) => l !== code));
  };

  const updateRoot = (index: number, patch: Partial<RootRow>) =>
    setRoots(roots.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const updatePlace = (index: number, patch: Partial<PlaceRow>) =>
    setPlaces(places.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const rootsValid = rootsStatus !== 'given' || filledRoots.length > 0;
  const grewUpValid = grewUpStatus !== 'given' || filledPlaces.length > 0;
  const isValid = Boolean(nativeLanguage) && rootsValid && grewUpValid;

  const handleSubmit = () => {
    if (!isValid) return;
    saveStep.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Icon Header */}
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-twins-light flex items-center justify-center">
          <Home className="w-8 h-8 text-twins-dark" />
        </div>
      </div>

      <p className="text-center text-gray-600 mb-6">
        Your Indian roots help us connect you with people from similar backgrounds
      </p>

      {/* Where the family is from */}
      <fieldset>
        <legend className="label">Where is your family from? *</legend>
        <div className="flex flex-wrap gap-2">
          {ROOTS_ANSWERS.map((answer) => (
            <button
              key={answer.value}
              type="button"
              onClick={() => setRootsStatus(answer.value)}
              aria-pressed={rootsStatus === answer.value}
              className={`px-3 py-1.5 rounded-full text-sm border ${
                rootsStatus === answer.value
                  ? 'bg-primary-100 border-primary-400 text-primary-800'
                  : 'border-gray-300 text-gray-700'
              }`}
            >
              {answer.label}
            </button>
          ))}
        </div>

        {rootsStatus === 'given' && (
          <div className="mt-3 space-y-3">
            {roots.map((row, index) => (
              <div key={index} className="flex flex-wrap gap-2 items-start">
                <select
                  aria-label={`Root ${index + 1}: kind`}
                  value={row.kind}
                  onChange={(e) => updateRoot(index, { kind: e.target.value as RootKind, value: '', town: '' })}
                  className="input w-auto"
                >
                  <option value="indian_state">An Indian state</option>
                  <option value="community">A community with no state</option>
                  <option value="place">A place outside today's India</option>
                  <option value="diaspora">A diaspora people</option>
                </select>

                {row.kind === 'indian_state' && (
                  <>
                    <select
                      aria-label={`Root ${index + 1}: state`}
                      value={row.value}
                      onChange={(e) => updateRoot(index, { value: e.target.value })}
                      className="input w-auto"
                    >
                      <option value="">Select a state</option>
                      {states.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label={`Root ${index + 1}: the family's town (optional)`}
                      type="text"
                      value={row.town}
                      onChange={(e) => updateRoot(index, { town: e.target.value })}
                      placeholder="Family town (optional)"
                      className="input w-auto"
                    />
                  </>
                )}
                {row.kind === 'community' && (
                  <select
                    aria-label={`Root ${index + 1}: community`}
                    value={row.value}
                    onChange={(e) => updateRoot(index, { value: e.target.value })}
                    className="input w-auto"
                  >
                    <option value="">Select a community</option>
                    {COMMUNITIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                )}
                {row.kind === 'diaspora' && (
                  <select
                    aria-label={`Root ${index + 1}: diaspora people`}
                    value={row.value}
                    onChange={(e) => updateRoot(index, { value: e.target.value })}
                    className="input w-auto"
                  >
                    <option value="">Select a people</option>
                    {DIASPORA.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                )}
                {row.kind === 'place' && (
                  <input
                    aria-label={`Root ${index + 1}: the place`}
                    type="text"
                    value={row.value}
                    onChange={(e) => updateRoot(index, { value: e.target.value })}
                    placeholder="e.g., Lahore, Dhaka"
                    className="input w-auto"
                  />
                )}

                {roots.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Remove root ${index + 1}`}
                    onClick={() => setRoots(roots.filter((_, i) => i !== index))}
                    className="p-2 text-gray-500 hover:text-gray-800"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {roots.length < MAX_ENTRIES && (
              <button
                type="button"
                onClick={() => setRoots([...roots, { kind: 'indian_state', value: '', town: '' }])}
                className="inline-flex items-center gap-1 text-sm text-primary-700"
              >
                <Plus className="w-4 h-4" /> Another side of the family
              </button>
            )}
          </div>
        )}

        <div className="mt-3">
          <label htmlFor="marriedInto" className="label">
            A family you married into <span className="text-gray-400">(optional, kept apart from your own roots)</span>
          </label>
          <select
            id="marriedInto"
            value={marriedInto}
            onChange={(e) => setMarriedInto(e.target.value)}
            className="input"
          >
            <option value="">None</option>
            {states.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </fieldset>

      {/* Where they grew up */}
      <fieldset>
        <legend className="label">Where did you grow up?</legend>
        <div className="flex flex-wrap gap-2">
          {GREW_UP_ANSWERS.map((answer) => (
            <button
              key={answer.value}
              type="button"
              onClick={() => setGrewUpStatus(answer.value)}
              aria-pressed={grewUpStatus === answer.value}
              className={`px-3 py-1.5 rounded-full text-sm border ${
                grewUpStatus === answer.value
                  ? 'bg-primary-100 border-primary-400 text-primary-800'
                  : 'border-gray-300 text-gray-700'
              }`}
            >
              {answer.label}
            </button>
          ))}
        </div>

        {grewUpStatus === 'given' && (
          <div className="mt-3 space-y-3">
            {places.map((row, index) => (
              <div key={index} className="flex flex-wrap gap-2 items-start">
                <input
                  aria-label={`Childhood ${index + 1}: country`}
                  type="text"
                  value={row.country}
                  onChange={(e) => updatePlace(index, { country: e.target.value, region: '' })}
                  placeholder="Country (IN, AE, Kenya)"
                  className="input w-32"
                />
                {row.country.trim().toUpperCase() === 'IN' && (
                  <select
                    aria-label={`Childhood ${index + 1}: state`}
                    value={row.region}
                    onChange={(e) => updatePlace(index, { region: e.target.value })}
                    className="input w-auto"
                  >
                    <option value="">State (optional)</option>
                    {states.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  aria-label={`Childhood ${index + 1}: town`}
                  type="text"
                  value={row.town}
                  onChange={(e) => updatePlace(index, { town: e.target.value })}
                  placeholder="Town"
                  className="input w-auto"
                />
                {places.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Remove childhood ${index + 1}`}
                    onClick={() => setPlaces(places.filter((_, i) => i !== index))}
                    className="p-2 text-gray-500 hover:text-gray-800"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {places.length < MAX_ENTRIES && (
              <button
                type="button"
                onClick={() => setPlaces([...places, { country: '', region: '', town: '' }])}
                className="inline-flex items-center gap-1 text-sm text-primary-700"
              >
                <Plus className="w-4 h-4" /> Another place
              </button>
            )}
          </div>
        )}
      </fieldset>

      {/* Decade (Optional) — stored on the Identity Board */}
      <div>
        <label htmlFor="era" className="label">
          The decade you grew up in <span className="text-gray-400">(optional)</span>
        </label>
        <select
          id="era"
          value={era}
          onChange={(e) => setEra(e.target.value)}
          className="input"
        >
          <option value="">Select a decade</option>
          {DECADE_OPTIONS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
      </div>

      {/* Native Language */}
      <div>
        <label htmlFor="nativeLanguage" className="label">
          Native/Mother Tongue *
        </label>
        <select
          id="nativeLanguage"
          value={nativeLanguage}
          onChange={(e) => setNativeLanguage(e.target.value)}
          className="input"
        >
          <option value="">Select your native language</option>
          {languageOptions.map((l) => (
            <option key={l.code} value={l.name}>
              {l.name} {l.nativeName && `(${l.nativeName})`}
            </option>
          ))}
        </select>
      </div>

      {/* Religion (Optional) — powers the festival calendar + reminders */}
      <div>
        <label htmlFor="religion" className="label">
          Religion <span className="text-gray-400">(optional — personalizes your festival calendar)</span>
        </label>
        <select
          id="religion"
          value={religion}
          onChange={(e) => setReligion(e.target.value)}
          className="input"
        >
          <option value="">Prefer not to say</option>
          {RELIGIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      {/* Other Languages */}
      <div>
        <label className="label">
          Other Languages <span className="text-gray-400">(select up to 5)</span>
        </label>

        {/* Selected Languages */}
        {languages.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {languages.map((code) => {
              const lang = languageOptions.find((l) => l.code === code);
              return (
                <span
                  key={code}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                >
                  {lang?.name || code}
                  <button
                    type="button"
                    onClick={() => handleRemoveLanguage(code)}
                    className="hover:bg-primary-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* Add Language Dropdown */}
        {languages.length < 5 && (
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) handleAddLanguage(e.target.value);
            }}
            className="input"
          >
            <option value="">+ Add another language</option>
            {languageOptions
              .filter((l) => !languages.includes(l.code) && l.name !== nativeLanguage)
              .map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
          </select>
        )}
      </div>

      {/* How do you say it? */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="label mb-0">How do you say it?</p>
            <p className="text-xs text-gray-500">
              The server composes the line from these answers only. {reviewedCount} reviewed people-name
              {reviewedCount === 1 ? '' : 's'} on the list; until a word is reviewed, lines name places.
            </p>
          </div>
          <button
            type="button"
            onClick={() => showWays.mutate(peopleName)}
            disabled={!rootsValid || showWays.isPending}
            className="btn-secondary text-sm whitespace-nowrap disabled:opacity-50"
          >
            {showWays.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Show the ways'}
          </button>
        </div>
        {showWays.isError && <p className="text-sm text-red-600">Could not compose the lines.</p>}
        {ways && (
          <>
            {ways.default ? (
              <p className="text-sm">
                Default: <strong>{ways.default.line}</strong>
                {ways.default.plates.length > 0 && (
                  <span className="ml-2 text-xs text-gray-500">plates {ways.default.plates.join(' ')}</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-gray-500">Nothing is shown for these answers.</p>
            )}
            {ways.options.length > 0 && (
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="declaration-choice" checked={choice === null} onChange={() => setChoice(null)} />
                  The default
                </label>
                {ways.options.map((option) => (
                  <label key={option.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="declaration-choice"
                      checked={choice === option.id}
                      onChange={() => setChoice(option.id)}
                    />
                    {option.line}
                    <span className="text-xs text-gray-400">{option.id}</span>
                  </label>
                ))}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="declaration-choice"
                    checked={choice === 'places_only'}
                    onChange={() => setChoice('places_only')}
                  />
                  Just my places
                </label>
              </div>
            )}
            {ways.peopleNames.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {ways.peopleNames.map((people) => (
                  <button
                    key={people.id}
                    type="button"
                    onClick={() => togglePeopleName(people.id)}
                    className={`px-2 py-1 rounded-full text-xs border ${
                      peopleName.includes(people.id) ? 'bg-primary-50 border-primary-300' : 'border-gray-300'
                    }`}
                  >
                    {people.singular}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Submit */}
      <div className="pt-4">
        <button
          onClick={handleSubmit}
          disabled={!isValid || saveStep.isPending}
          className="btn-primary w-full py-3 disabled:opacity-50"
        >
          {saveStep.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Complete & Build Identity Board'
          )}
        </button>
        {saveStep.isError && (
          <p className="text-sm text-red-600 mt-2 text-center">
            Failed to save. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
